import { useState } from 'react';
import axios from 'axios';
import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { InfoInformationCircleIcon, DocumentFileCurvedCornerIcon } from '@qitech/icons';

import { ErrorPage } from '../../../ErrorPage';
import { PrivatePayrollAuctionLoading } from '../Loading';
import { useThemeContext } from '../../../../contexts/ThemeContext';
import { getOrCreateBrowserKey } from '../../../../utils/fingerprint_utils';
import { useScrollToBottom } from '../../../../utils/hooks/useScrollToBottom';
import { useAnimatedModal } from '../../../../utils/hooks/useAnimatedModal';
import { resolveColor } from '../../theme/utils/resolveColor';
import { PoweredByQITech } from '../components/PoweredByQITech';
import { CheckboxField } from '../components/CheckboxField';
import { PrivatePayrollAuctionDocumentViewer } from '../components/DocumentViewer';
import * as S from './styles';

interface RefinancingSummaryProps {
    signData: any;
    setSignerStep: (step: string) => void;
    setSignData: (data: any) => void;
}

const formatDate = (inputDate: string): string => {
    const [year, month, day] = inputDate.split('-');
    return `${day}/${month}/${year}`;
};

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

const formatPercent = (value: number): string =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

const ButtonSpinner = ({ color = '#ffffff' }: { color?: string }) => (
    <>
        <style>{`@keyframes qiBtnSpin{to{transform:rotate(360deg);}}`}</style>
        <span
            aria-hidden
            style={{
                display: 'inline-block',
                width: 16,
                height: 16,
                border: `2px solid ${color}55`,
                borderTopColor: color,
                borderRadius: '50%',
                animation: 'qiBtnSpin 0.8s linear infinite',
            }}
        />
    </>
);

interface ContractAccordionProps {
    contract: any;
    defaultOpen: boolean;
    primary: string;
}

const ContractAccordion = ({ contract, defaultOpen, primary }: ContractAccordionProps) => {
    const [open, setOpen] = useState(defaultOpen);
    const co = contract?.credit_operation;

    return (
        <S.Accordion $open={open} $color={primary}>
            <S.AccordionHead
                type="button"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <S.AccordionIcon $color={primary}>
                    <DocumentFileCurvedCornerIcon size={17} />
                </S.AccordionIcon>
                <S.AccordionHeadLeft>
                    <S.AccordionTitle $color={primary}>Empréstimo</S.AccordionTitle>
                    {contract?.contract_number && (
                        <S.AccordionSub>Contrato {contract.contract_number}</S.AccordionSub>
                    )}
                </S.AccordionHeadLeft>
                <S.AccordionHeadRight>
                    <S.AccordionEmissionLabel>Valor de emissão</S.AccordionEmissionLabel>
                    <S.AccordionEmissionValue $color={primary}>
                        {formatCurrency(co?.issue_amount ?? 0)}
                    </S.AccordionEmissionValue>
                </S.AccordionHeadRight>
                <S.AccordionChevron $open={open} $color={primary}>
                    <ChevronDownIcon width={18} height={18} />
                </S.AccordionChevron>
            </S.AccordionHead>
            <S.AccordionBody $open={open}>
                <div className="inner">
                    <S.DataRow>
                        <S.RowLabel>Saldo devedor</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(contract?.due_balance ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>IOF</S.RowLabel>
                        <S.RowValue $color={primary}>{formatCurrency(co?.iof ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(co?.monthly_interest_rate ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(co?.monthly_cet ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Parcelas restantes</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {contract?.number_of_remaining_installments != null
                                ? `${contract.number_of_remaining_installments} parcelas`
                                : '-'}
                        </S.RowValue>
                    </S.DataRow>
                </div>
            </S.AccordionBody>
        </S.Accordion>
    );
};

export const PrivatePayrollAuctionRefinancingSummary = ({
    signData,
    setSignerStep,
    setSignData,
}: RefinancingSummaryProps) => {
    const { clientThemeSettings } = useThemeContext();
    const primary = resolveColor('primary', clientThemeSettings);
    const text = resolveColor('text', clientThemeSettings);
    const initialError = { has_error: false, status: 0 };

    const [error, setError] = useState<{ has_error: boolean; status: number }>(initialError);
    const [loading, setLoading] = useState(false);
    const [hasMadeRequest, setHasMadeRequest] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isAwaitingUrls, setIsAwaitingUrls] = useState(false);
    const [viewerUrl, setViewerUrl] = useState<string | null | undefined>(undefined);

    const op = signData?.refinancing_operation;
    const currentContracts: any[] = op?.current_contracts ?? [];
    const newContract = op?.new_contract;
    const newOp = newContract?.credit_operation;
    const documentUrl: string | null | undefined = signData?.document_url;
    const hasDocumentUrlKey = signData != null && 'document_url' in signData;

    const many = currentContracts.length > 1;

    const { contentEndRef, hasScrolledToEnd, scrollToBottom } = useScrollToBottom([]);
    const checkboxModal = useAnimatedModal();

    const hasPendingDocumentUrls = (data: any): boolean =>
        data != null && 'document_url' in data && data.document_url === null;

    const waitForDocumentUrls = async (): Promise<{ ok: boolean; data: any }> => {
        if (!hasPendingDocumentUrls(signData)) return { ok: true, data: signData };
        setIsAwaitingUrls(true);
        const config = { headers: { Authorization: signData.signer_token } };
        const startedAt = Date.now();
        let merged: any = signData;
        try {
            while (Date.now() - startedAt < 120000) {
                await new Promise((r) => setTimeout(r, 15000));
                try {
                    const r = await axios.get('/signer', config);
                    merged = { ...merged, ...r.data };
                    setSignData(merged);
                    if (!hasPendingDocumentUrls(r.data)) {
                        return { ok: true, data: merged };
                    }
                } catch {}
            }
            return { ok: false, data: merged };
        } finally {
            setIsAwaitingUrls(false);
        }
    };

    const pollForDocumentUrl = async (): Promise<string | null | undefined> => {
        const config = { headers: { Authorization: signData.signer_token } };
        const r = await axios.get('/signer', config);
        setSignData({ ...signData, ...r.data });
        return r.data.document_url;
    };

    const handleSubmit = async () => {
        if (hasMadeRequest || isAwaitingUrls) return;

        const urlsResult = await waitForDocumentUrls();
        if (!urlsResult.ok) {
            setError({ has_error: true, status: 500 });
            return;
        }

        let browserKey: string | null = null;
        try {
            browserKey = await getOrCreateBrowserKey();
        } catch (_) {}

        const payload = {
            step: 'refinancing_summary',
            event_date: new Date().toISOString(),
            device_data: { device_key: browserKey },
        };

        const config = { headers: { Authorization: signData.signer_token } };

        setLoading(true);
        setHasMadeRequest(true);

        axios
            .patch('/signer', payload, config)
            .then((response) => {
                setSignerStep(response.data.next_step);
                setSignData({ ...signData, ...response.data });
            })
            .catch((err) => {
                const status = [401, 403, 409].includes(err.response?.status)
                    ? err.response.status
                    : 500;
                setError({ has_error: true, status });
                setHasMadeRequest(false);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleButtonClick = () => {
        if (!hasScrolledToEnd) {
            scrollToBottom();
            return;
        }

        if (!isChecked) {
            checkboxModal.open();
            return;
        }

        handleSubmit();
    };

    if (loading) return <PrivatePayrollAuctionLoading />;

    if (viewerUrl !== undefined) {
        return (
            <PrivatePayrollAuctionDocumentViewer
                url={viewerUrl ?? null}
                title="Contrato de refinanciamento"
                onBack={() => setViewerUrl(undefined)}
                pollForUrl={viewerUrl === null ? pollForDocumentUrl : undefined}
            />
        );
    }

    if (error.has_error) {
        return (
            <ErrorPage
                error={error}
                redirectToLogin={() => setSignerStep('login')}
                resetError={() => setError(initialError)}
                clientThemeSettings={clientThemeSettings}
            />
        );
    }

    return (
        <S.PageWrapper>
            <S.LogoSection>
                {clientThemeSettings?.logo_url && (
                    <S.LogoImage src={clientThemeSettings.logo_url} alt="logo" />
                )}
            </S.LogoSection>

            <S.ScrollContent>
                <S.PageHeader>
                    <S.Heading $color={primary}>Melhore as condições do seu empréstimo</S.Heading>
                    <S.Subtitle>
                        {many
                            ? 'Refinancie os seus contratos atuais com condições melhores e ainda receba um valor extra na conta.'
                            : 'Refinancie o seu contrato atual com condições melhores e ainda receba um valor extra na conta.'}
                    </S.Subtitle>
                </S.PageHeader>

                <S.TrocoHero $color={primary}>
                    <S.TrocoLead $color={primary}>
                        Você recebe na conta
                    </S.TrocoLead>
                    <S.TrocoValue $color={primary}>
                        {formatCurrency(newContract?.final_disbursed_amount ?? 0)}
                    </S.TrocoValue>
                    <S.TrocoCaption>
                        Valor adicional liberado no refinanciamento, creditado direto na sua conta.
                    </S.TrocoCaption>
                </S.TrocoHero>

                <S.Caption>Condições da nova operação</S.Caption>

                <S.AccentCard $color={primary}>
                    <S.CardHead $color={primary}>
                        Condições do refinanciamento
                    </S.CardHead>

                    <S.MathInset $color={primary}>
                        <S.MathRow $color={primary}>
                            <S.MathLabel $color={primary}>Novo valor de emissão</S.MathLabel>
                            <S.MathValue $color={primary}>{formatCurrency(newOp?.issue_amount ?? 0)}</S.MathValue>
                        </S.MathRow>
                        <S.MathRow $color={primary}>
                            <S.MathLabel $color={primary}>
                                <S.MathOp $color={primary}>−</S.MathOp>
                                Empréstimos antigos
                            </S.MathLabel>
                            <S.MathValue $color={primary}>{formatCurrency(newContract?.total_due_balance ?? 0)}</S.MathValue>
                        </S.MathRow>
                        <S.MathRow $color={primary}>
                            <S.MathLabel $color={primary}>
                                <S.MathOp $color={primary}>−</S.MathOp>
                                IOF
                            </S.MathLabel>
                            <S.MathValue $color={primary}>{formatCurrency(newOp?.iof ?? 0)}</S.MathValue>
                        </S.MathRow>
                        <S.MathRow $total $color={primary}>
                            <S.MathLabel $total $color={primary}>
                                <S.MathOp $color={primary}>=</S.MathOp>
                                Você recebe na conta
                            </S.MathLabel>
                            <S.MathValue $total $color={primary}>
                                {formatCurrency(newContract?.final_disbursed_amount ?? 0)}
                            </S.MathValue>
                        </S.MathRow>
                    </S.MathInset>

                    <S.DataRow>
                        <S.RowLabel>CET (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(newOp?.monthly_cet ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Taxa de juros (ao mês)</S.RowLabel>
                        <S.RowValue $color={primary}>{formatPercent(newOp?.monthly_interest_rate ?? 0)}</S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Parcelas mensais</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {newOp?.number_of_installments != null
                                ? `${newOp.number_of_installments} parcelas`
                                : '-'}
                        </S.RowValue>
                    </S.DataRow>
                    <S.DataRow>
                        <S.RowLabel>Primeira parcela</S.RowLabel>
                        <S.RowValue $color={primary}>
                            {newOp?.first_due_date ? formatDate(newOp.first_due_date) : '-'}
                        </S.RowValue>
                    </S.DataRow>
                </S.AccentCard>

                {hasDocumentUrlKey && (
                    <S.InfoCallout $color={primary}>
                        <S.IconWrapper $color={primary}>
                            <DocumentFileCurvedCornerIcon size={24} />
                        </S.IconWrapper>
                        <S.InfoText $color={primary}>
                            Para mais informações,{' '}
                            <a
                                href={documentUrl || '#'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setViewerUrl(documentUrl ?? null);
                                }}
                            >
                                consulte o contrato da operação de refinanciamento.
                            </a>
                        </S.InfoText>
                    </S.InfoCallout>
                )}

                <S.Caption>{many ? 'Seus contratos sendo refinanciados' : 'Seu contrato sendo refinanciado'}</S.Caption>

                <S.AccordionList>
                    {currentContracts.map((contract, i) => (
                        <ContractAccordion
                            key={i}
                            contract={contract}
                            defaultOpen={!many}
                            primary={primary}
                        />
                    ))}
                </S.AccordionList>

                <S.CheckboxWrapper>
                    <CheckboxField
                        id="refinancing-summary-terms"
                        checked={isChecked}
                        onChange={(e) => setIsChecked(e.target.checked)}
                        checkboxColor={primary}
                        textColor={text}
                    >
                        <strong style={{ color: primary }}>Confirmo que li e estou de acordo</strong> com as condições de contratação do meu refinanciamento.
                    </CheckboxField>
                </S.CheckboxWrapper>

                <div ref={contentEndRef} />
            </S.ScrollContent>

            <S.Footer>
                <S.SubmitButton
                    $backgroundColor={primary}
                    $disabled={!hasScrolledToEnd || !isChecked || isAwaitingUrls}
                    onClick={handleButtonClick}
                >
                    {isAwaitingUrls ? (
                        <>
                            <ButtonSpinner />
                            Preparando documentos...
                        </>
                    ) : (
                        <>
                            Refinanciar meu empréstimo
                            <ChevronRightIcon width={20} height={20} />
                        </>
                    )}
                </S.SubmitButton>
                <PoweredByQITech />
            </S.Footer>

            {checkboxModal.show && (
                <S.ModalOverlay $closing={checkboxModal.isClosing} onClick={checkboxModal.close}>
                    <S.ModalSheet $closing={checkboxModal.isClosing} onClick={(e) => e.stopPropagation()}>
                        <S.SheetHandle />
                        <S.ModalTitle $color={primary}>Confirmação pendente</S.ModalTitle>
                        <S.ModalText style={{ textAlign: 'start' }}>
                            Para continuar, você precisa marcar a seguinte caixa acima:
                            <ul>
                                <li><strong>Aceite das condições do refinanciamento</strong></li>
                            </ul>
                        </S.ModalText>
                        <S.ModalButtonRow>
                            <S.SubmitButton
                                $backgroundColor={primary}
                                $disabled={false}
                                onClick={checkboxModal.close}
                            >
                                Entendi
                                <ChevronRightIcon width={24} height={24} />
                            </S.SubmitButton>
                        </S.ModalButtonRow>
                    </S.ModalSheet>
                </S.ModalOverlay>
            )}
        </S.PageWrapper>
    );
};
